/**
 * Identity helpers for the (dormant) sync layer.
 *
 * Generates stable, random, NON-PII identifiers:
 *   - profileId: the pseudonymous learning-profile id. It carries no link to
 *     a child's real identity — see the privacy-by-design split (auth identity
 *     vs learning profile).
 *   - deviceId: a per-install id, used for sync cursors and multi-device
 *     merge bookkeeping.
 *
 * Pure + dependency-free: uses Web Crypto `randomUUID` (browsers + Node 19+),
 * with an RFC-4122 v4 fallback built on `getRandomValues`. Nothing here reads
 * or writes storage; callers own persistence.
 */

/**
 * Generate a random UUID (v4). Throws only if no secure RNG exists at all,
 * which should never happen in a supported runtime.
 */
export function generateId() {
  const g = typeof globalThis !== 'undefined' ? globalThis : {};
  const c = g.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    // Set the version (4) and variant (10xx) bits per RFC 4122.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return (
      `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-` +
      `${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-` +
      `${hex.slice(10, 16).join('')}`
    );
  }
  throw new Error('No secure random source available for id generation.');
}

/**
 * Return an identity object with both ids present. Existing valid ids are
 * preserved; only missing/invalid ones are freshly generated. Idempotent —
 * calling it again with its own output returns the same ids. This is the
 * shape the dormant schema-v8 migration will fill in on first boot.
 *
 * @param {{ profileId?: string, deviceId?: string }} [identity]
 * @returns {{ profileId: string, deviceId: string }}
 */
export function ensureIdentity(identity) {
  const safe = identity && typeof identity === 'object' ? identity : {};
  const keep = (v) => (typeof v === 'string' && v.length > 0 ? v : generateId());
  return {
    profileId: keep(safe.profileId),
    deviceId: keep(safe.deviceId),
  };
}
